'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import {
  bookSizesFor,
  bookSizeOf,
  clampPageCount,
  minutesForBlocks,
  pageRangeFor,
  resizeForAge,
  type BookSize,
} from '@/contexts/stories/utils/storyShape';

/**
 * How long the story is — in PAGES, because that is the only length the
 * pipeline understands and the only one that costs money.
 *
 * The question used to be minutes, which the writer never sees: the story step
 * is told `exactly {{ inputs.blockCount }} blocks`, and the minutes were turned
 * into that number behind the parent's back. It made the two numbers that
 * matter disagree — a 7-minute default is a 28-page book, and the welcome gift
 * covers fifteen pages — and the control was only drawn for orders with
 * narration, so a book-only order never even saw the choice it was making.
 *
 * Three named sizes plus a number for anyone who wants an exact one. Minutes
 * stay on screen as a consequence of the choice, not as the choice.
 */
export function BookSizeChoice({
  pages,
  ageGroup,
  language,
  onChange,
  compact = false,
}: {
  pages: number;
  ageGroup?: string;
  language?: string;
  onChange: (pages: number) => void;
  /** The dense two-column grid of the one-screen story form. */
  compact?: boolean;
}) {
  const t = useTranslations('content.wizard');
  const sizes = bookSizesFor(ageGroup);
  const range = pageRangeFor(ageGroup);
  const active = bookSizeOf(pages, ageGroup);
  const minutes = minutesForBlocks(pages, ageGroup, language);

  /**
   * The age decides what a page holds, so it decides what the sizes are. When
   * the parent corrects the age, a chosen SIZE follows it by name (medium stays
   * medium) and a hand-typed number is pulled into what the new age allows.
   */
  const lastAge = useRef(ageGroup);
  useEffect(() => {
    if (lastAge.current === ageGroup) return;
    const previous = lastAge.current;
    lastAge.current = ageGroup;
    const next = resizeForAge(pages, previous, ageGroup);
    if (next !== pages) onChange(next);
  }, [ageGroup, pages, onChange]);

  return (
    <div className="space-y-2" data-testid="content-book-size">
      <Label className={compact ? 'text-xs sm:text-sm' : undefined}>{t('parameters.length')}</Label>
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(sizes) as BookSize[]).map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => onChange(sizes[size])}
            aria-pressed={active === size}
            data-testid={`content-book-size-${size}`}
            className={`rounded-xl border px-3 py-2 text-left text-xs transition-colors sm:text-sm ${
              active === size ? 'border-primary bg-primary/10 font-semibold text-primary' : 'hover:bg-muted'
            }`}
          >
            <span className="block">{t(`parameters.bookSizes.${size}`)}</span>
            <span className="block text-[11px] font-normal text-muted-foreground">
              {t('parameters.pagesValue', { pages: sizes[size] })}
            </span>
          </button>
        ))}
        <label className="flex items-center gap-2 text-xs text-muted-foreground sm:text-sm">
          <span className="sr-only sm:not-sr-only">{t('parameters.pagesOwn')}</span>
          <Input
            type="number"
            inputMode="numeric"
            min={range.min}
            max={range.max}
            value={pages}
            data-testid="content-book-pages-input"
            // Clamping on change would fight a parent typing "1" on the way to
            // "12", so the field takes the digits and the blur settles them.
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isFinite(next)) onChange(next);
            }}
            onBlur={(e) => onChange(clampPageCount(Number(e.target.value), ageGroup))}
            className="h-10 w-20"
            aria-label={t('parameters.pagesOwn')}
          />
        </label>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="content-book-size-hint">
        {t('parameters.lengthHint', { pages: clampPageCount(pages, ageGroup), minutes })}
      </p>
    </div>
  );
}
